import React, { useContext, useMemo, useState, useEffect } from 'react';
import { KatSpinner, KatDataTable, KatPagination } from '@amzn/katal-react';
import { useTranslation } from 'src/i18next-arb-shim/useTranslation';
import { AuthContext } from 'src/components/auth/AuthContext';
import '../SourceReviewTables.scss';
import {
  AuthorizedStringGroupsInput,
  useAuthorizedStringGroupsQuery,
} from 'src/generated/graphql';
import { map } from 'lodash';
import openReviewsColumnConfig from './OpenReviewsColumnConfig';
import { useHistory, useLocation } from 'react-router-dom';
import emptyInbox from 'src/images/empty-inbox.png';
import {
  getPaginatedData,
  getDisplayRange,
  formatOpenReviewRow,
} from '../Utils';

type Props = {
  id: string;
};

/**
 * Open Reviews Table
 *
 * Displays open source review requests for authorized string groups.
 */
const OpenReviewsTable: React.FC<Props> = (props: Props) => {
  const { t, i18n } = useTranslation();
  const { userId } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();
  const state = location.state as {
    approvalStatus?: string;
    messageGroupName?: string;
  };

  const params: AuthorizedStringGroupsInput = {
    userAlias: userId,
  };

  /* Gql Hooks*/
  // Get string groups for user that are pending review
  const {
    data: authorizedStringGroupsData,
    loading: authorizedStringGroupsLoading,
    error: authorizedStringGroupsError,
    refetch: refetchAuthorizedStringGroupsData,
  } = useAuthorizedStringGroupsQuery({
    variables: { params },
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  // States for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Refetch data after successful approval
  useEffect(() => {
    if (state?.approvalStatus) {
      refetchAuthorizedStringGroupsData();
      setCurrentPage(1);

      // Clear the state after using it to prevent the success message from showing on refresh
      history.replace({
        pathname: location.pathname,
        search: location.search,
      });
    }
  }, [
    state?.approvalStatus,
    refetchAuthorizedStringGroupsData,
    history,
    location.pathname,
    location.search,
  ]);

  // Transform data to display
  const rowData = useMemo(() => {
    return map(
      authorizedStringGroupsData?.authorizedStringGroups.stringGroups,
      (row) => formatOpenReviewRow(row, i18n.language)
    );
  }, [authorizedStringGroupsData, i18n.language]);

  const totalItems = rowData?.length || 0;

  // Handle changes to number of items per page
  const handleItemsPerPageChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newValue = parseInt(e.target.value, 10);
    setItemsPerPage(newValue);
    setCurrentPage(1);
  };

  // Handle page change
  const onPageChange = (e: KatPagination.ChangeEvent) => {
    const newPage = (e.target as any).page as number;
    setCurrentPage(newPage);
  };

  // Get data for current page based on pagination settings
  const currentPageData = useMemo(
    () => getPaginatedData(rowData, currentPage, itemsPerPage),
    [rowData, currentPage, itemsPerPage]
  );

  // Loading Page
  if (authorizedStringGroupsLoading) {
    return (
      <>
        <KatDataTable
          id={props.id + '-table'}
          columns={openReviewsColumnConfig(t, history)}
          titleLabel={t('source-review-table-title')}
        ></KatDataTable>
        <KatSpinner id={props.id + '-spinner'} />
      </>
    );
  }

  // Error response page
  if (authorizedStringGroupsError) {
    return (
      <>
        <KatDataTable
          id={props.id + '-table'}
          columns={openReviewsColumnConfig(t, history)}
          titleLabel={t('source-review-table-title')}
        ></KatDataTable>

        <div id={props.id + '-error-response'}>
          <div id={props.id + '-error-symbol'}>!</div>
          <div id={props.id + '-error-text'}>
            {t('source-review-error-response-string')}
          </div>
        </div>
      </>
    );
  }

  // Empty response page
  if (totalItems === 0) {
    return (
      <>
        <KatDataTable
          id={props.id + '-table'}
          columns={openReviewsColumnConfig(t, history)}
          titleLabel={t('source-review-table-title')}
        ></KatDataTable>
        <div id={props.id + '-empty-response'}>
          <img
            src={emptyInbox}
            alt="No Review Requests"
            id={props.id + '-empty-icon'}
          />
          <div id={props.id + '-empty-text'}>
            {t('source-review-empty-response-string')}
          </div>
        </div>
      </>
    );
  }

  // Happy case - show string groups with Review button and handle pagination
  return (
    <>
      <KatDataTable
        id={props.id + '-table'}
        columns={openReviewsColumnConfig(t, history)}
        rowData={currentPageData}
        titleLabel={t('source-review-table-title')}
      />
      {/* Pagination controls container */}
      <div id={props.id + '-pagination-container'}>
        {/* Display range showing current items */}
        <div id={props.id + '-display-range'}>
          {t('source-review-requests-range-prefix')}{' '}
          {getDisplayRange(currentPage, itemsPerPage, totalItems)}
        </div>
        {/* Pagination component for navigating between pages */}
        <div id={props.id + '-pagination-wrapper'}>
          <KatPagination
            id={props.id + '-pagination'}
            page={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onChange={onPageChange}
          />
        </div>
        {/* Items per page selector */}
        <div id={`${props.id}-items-per-page`}>
          {t('source-review-results-per-page')}&nbsp;
          <select
            id={`${props.id}-items-per-page-select`}
            value={itemsPerPage.toString()}
            onChange={handleItemsPerPageChange}
          >
            <option value="10">10</option>
            <option value="20">20</option>
          </select>
        </div>
      </div>
    </>
  );
};

export default OpenReviewsTable;
